import { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useSWR from 'swr';
import api from '../services/api';
import history from '../services/history';
import { selectProntuarioById } from '../redux/features/prontuario/prontuarioSlice';
import { liberarLaudoById } from '../services/prontuario';
import selectEcoById from '../services/ecodoppler';

// Fetcher padrão para SWR
const fetcher = url => api.get(url).then(res => res.data);
const EMPTY_ATIVIDADES = [];

const useAtividades = ({ idMedico, medico, syncUrl = true } = {}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [loadingAction, setLoadingAction] = useState(false);
  const [selectedPac, setSelectedPac] = useState({ codPac: null, nomePac: '' });

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const isModalOpenURL = syncUrl && searchParams.get('modalOpen') === 'true';
  const urlCodPac = syncUrl ? searchParams.get('userId') : null;
  const urlNomePac = syncUrl ? searchParams.get('pac') : null;

  const activeCodPac = selectedPac.codPac || urlCodPac;
  const activeNomePac = selectedPac.nomePac || urlNomePac || '';
  const shouldFetch = activeCodPac && activeCodPac !== '1' && activeCodPac !== 1;
  const {
    data: atividades = EMPTY_ATIVIDADES,
    isValidating: loadingSWR,
    mutate: mutateAtividades,
  } = useSWR(shouldFetch ? `pacientes/${activeCodPac}/atividades` : null, fetcher, {
    revalidateOnFocus: false,
    onError: err => {
      toast.error(err?.response?.data?.error || 'Erro ao carregar atividades!');
    },
  });

  const showAtividadesModal = Boolean(isModalOpenURL || selectedPac.codPac);

  // 3. Modais e Navegação
  const openAtividades = useCallback(
    (codPac, nomePac = '') => {
      setSelectedPac({ codPac, nomePac });
      if (!syncUrl) return;

      const params = new URLSearchParams(location.search);
      const currentDate = params.get('date');

      params.set('modalOpen', 'true');
      params.set('userId', codPac);
      if (nomePac) params.set('pac', nomePac);
      if (idMedico) params.set('med', idMedico);
      if (currentDate) params.set('date', currentDate);

      history.push(`${location.pathname}?${params.toString()}`);
    },
    [idMedico, location.pathname, location.search, syncUrl]
  );

  const closeAtividadesModal = useCallback(() => {
    setSelectedPac({ codPac: null, nomePac: '' });
    if (!syncUrl) return;

    const params = new URLSearchParams(location.search);
    params.delete('modalOpen');
    params.delete('userId');
    params.delete('pac');

    const newQuery = params.toString();
    history.push(
      newQuery ? `${location.pathname}?${newQuery}` : location.pathname
    );
  }, [location.pathname, location.search, syncUrl]);

  const handleAtividades = useCallback(
    atividade => {
      openAtividades(atividade.CODPAC, atividade.NOMEPAC);
    },
    [openAtividades]
  );

  const handleLaudarCongenitoTXT = useCallback(atividade => {
    history.push(`/formCongenitoTXT/${atividade.CODLAUDO}`);
  }, []);

  const handleLaudar = useCallback(
    async atividade => {
      setLoadingAction(true);
      try {
        switch (atividade.CODPROCEDI) {
          case 1:
          case 58:
          case 800:
            await dispatch(selectProntuarioById(atividade.CODLAUDO));
            history.push('/formProntuario');
            break;
          case 3:
            history.push(`/formEcg/${atividade.CODLAUDO}`);
            break;
          case 6:
          case 820:
            history.push(`/reportHolter/${atividade.CODLAUDO}`);
            break;
          case 7:
          case 810:
            history.push(`/reportMapa/${atividade.CODLAUDO}`);
            break;
          case 13:
            history.push('/formStress', { codLaudo: atividade.CODLAUDO, medico });
            break;
          case 12:
            history.push(`/form-trans/${atividade.CODLAUDO}`);
            break;
          case 4:
            history.push(`/formErgo/${atividade.CODLAUDO}`);
            break;
          case 11:
          case 712:
          case 713:
          case 714: {
            const eco = await selectEcoById(atividade.CODLAUDO);
            if (eco && eco.TIPOECO === 'EcoC') {
              history.push(`/formCongenito/${atividade.CODLAUDO}`);
            } else {
              history.push(`/formDoppler/${atividade.CODLAUDO}`);
            }
            break;
          }
          case 14:
          case 15:
            history.push(`/form-carotida/${atividade.CODLAUDO}`);
            break;
          case 17:
            history.push('/form-fetal', { codLaudo: atividade.CODLAUDO });
            break;
          case 2:
            history.push({
              pathname: '/form-avaliacao',
              state: { codLaudo: atividade.CODLAUDO },
            });
            break;
          default:
            toast.warn('Laudo não disponível para registro!');
            break;
        }
      } catch (error) {
        toast.error(error?.response?.data?.error || 'Erro ao carregar laudo!');
      } finally {
        setLoadingAction(false);
      }
    },
    [dispatch, medico]
  );

  const handleLiberarLaudo = useCallback(
    async (atividade, liberado) => {
      try {
        setLoadingAction(true);
        const fatura = { codFatura: atividade.CODFATURA, liberado };
        const res = await liberarLaudoById({ fatura });
        toast.success(res.data?.message || 'Laudo atualizado!');
        mutateAtividades();
      } catch (error) {
        toast.error(error?.response?.data?.error || 'Erro ao liberar laudo!');
      } finally {
        setLoadingAction(false);
      }
    },
    [mutateAtividades]
  );

  // 4. Objeto de Ações Agrupado para evitar Prop Drilling
  const atividadesActions = useMemo(
    () => ({
      handleLaudar,
      handleLaudarCongenitoTXT,
      handleLiberarLaudo,
    }),
    [handleLaudar, handleLaudarCongenitoTXT, handleLiberarLaudo]
  );

  return {
    atividades,
    nomePac: activeNomePac,
    codPac: activeCodPac,
    showAtividadesModal,
    openAtividades,
    closeAtividadesModal,
    handleAtividades,
    loadingAtividades: loadingSWR || loadingAction,
    mutateAtividades,
    atividadesActions, // Exportado pronto para consumo
  };
};

export default useAtividades;
